import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { LeadLogType } from '../../types/leads/lead-log.type';
import { LeadType } from '../../types/leads/lead.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import { getLeadLogsList, getLeadsList } from './leads.action';

type LeadsRouteParams = RouteParamsType & { esi_id: string | number };
type LeadLogsRouteParams = RouteParamsType;

interface LeadsState {
  leadsList: PaginationType<LeadType> | null;
  leadsListFetchStatus: FETCH_STATUS;
  leadsRouteParams: LeadsRouteParams;
  leadLogsList: PaginationType<LeadLogType> | null;
  leadLogsListFetchStatus: FETCH_STATUS;
  leadLogsRouteParams: LeadLogsRouteParams;
}

export const initialLeadsRouteParams: LeadsRouteParams = {
  page: 1,
  limit: 10,
  order: 'id',
  q: '',
  type: '',
  esi_id: '',
};

export const initialLeadLogsRouteParams: LeadLogsRouteParams = {
  page: 1,
  limit: 10,
  order: '-createdAt',
  q: '',
  type: '',
};

const INIT_STATE: LeadsState = {
  leadsList: null,
  leadsListFetchStatus: FETCH_STATUS.NULL,
  leadsRouteParams: initialLeadsRouteParams,
  leadLogsList: null,
  leadLogsListFetchStatus: FETCH_STATUS.NULL,
  leadLogsRouteParams: initialLeadLogsRouteParams,
};

const leadsSlice = createSlice({
  name: 'leads',
  initialState: INIT_STATE,
  reducers: {
    setLeadsList: (state, action) => {
      state.leadsList = action.payload;
    },
    setLeadsListFetchStatus: (state, action) => {
      state.leadsListFetchStatus = action.payload;
    },
    setLeadLogsList: (state, action) => {
      state.leadLogsList = action.payload;
    },
    setLeadLogsListFetchStatus: (state, action) => {
      state.leadLogsListFetchStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'leads') {
        state.leadsRouteParams = { ...state.leadsRouteParams, ...payload.data };
      }
      if (payload.reducer === 'leadLogs') {
        state.leadLogsRouteParams = { ...state.leadLogsRouteParams, ...payload.data };
      }
    });

    builder
      .addCase(getLeadsList.pending, state => {
        state.leadsListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getLeadsList.fulfilled, (state, action) => {
        state.leadsListFetchStatus = FETCH_STATUS.FULFILLED;
        state.leadsList = action.payload;
      })
      .addCase(getLeadsList.rejected, (state, action) => {
        state.leadsListFetchStatus = FETCH_STATUS.REJECTED;
        state.leadsList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      })
      .addCase(getLeadLogsList.pending, state => {
        state.leadLogsListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getLeadLogsList.fulfilled, (state, action) => {
        state.leadLogsListFetchStatus = FETCH_STATUS.FULFILLED;
        state.leadLogsList = action.payload;
      })
      .addCase(getLeadLogsList.rejected, (state, action) => {
        state.leadLogsListFetchStatus = FETCH_STATUS.REJECTED;
        state.leadLogsList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      });
  },
});

export const { setLeadsList, setLeadsListFetchStatus, setLeadLogsList, setLeadLogsListFetchStatus } =
  leadsSlice.actions;

export default leadsSlice.reducer;
